import type { StatusColorName, StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCancel,
  mdiCheckBold,
  mdiCloseCircleOutline,
  mdiClockOutline,
  mdiEmailOutline,
  mdiLockOutline,
  mdiMessageTextOutline,
  mdiProgressClock,
  mdiSendClockOutline,
  mdiTimerOffOutline,
} from "@mdi/js"

import type {
  AdaptedCommunicationPlanFeedEntry,
  CaseCommunicationTimelineItem,
  ContactStepOutcomeReadModel,
  KehrblattCaseCommunicationExtensionData,
} from "../../../../../../../../api/kehrblatt/case-communications"
import type { ContactOrderStep } from "../../../../../../../../api/kehrblatt/contact-orders"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import type { ContactOrderPlanHint } from "../../../_logic/communication-plan-table-contract"

export type CommunicationPlanStatusPresentation = {
  definition: StatusDefinition<string>
  subLabel: string | null
  tooltipHtml: string
  hint: ContactOrderPlanHint | null
}

type ReadyDueState =
  | { kind: "future"; formattedDueAt: string }
  | { kind: "overdue"; formattedDueAt: string }
  | { kind: "unknown"; formattedDueAt: null }

const normalizedCode = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
const normalizedText = (value: unknown) => String(value ?? "").trim() || null

const outcomeActorTypeLabels: Record<string, string> = {
  ADMIN: "Admin",
  BOT: "Bot",
  PORTAL: "Terminportal",
  SYSTEM: "System",
  UNKNOWN: "Unbekannt",
}

export const displayOutcomeActor = (recordedBy?: ContactStepOutcomeReadModel["recorded_by"]) =>
  recordedBy ? recordedBy.label || outcomeActorTypeLabels[recordedBy.actor_type] : null

const formatDateTime = (value?: string | null) => {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })
}

const formatDate = (value?: string | null) => {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
}

const classifyReadyDueAt = (value: ContactOrderStep["due_at"], now: number): ReadyDueState => {
  if (!value) return { kind: "unknown", formattedDueAt: null }

  const dueAt = new Date(value)
  const formattedDueAt = formatDateTime(value)

  if (Number.isNaN(dueAt.getTime()) || !formattedDueAt) {
    return { kind: "unknown", formattedDueAt: null }
  }

  return {
    kind: dueAt.getTime() > now ? "future" : "overdue",
    formattedDueAt,
  }
}

const readyDueSubLabel = (value: ContactOrderStep["due_at"], now: number): string => {
  const state = classifyReadyDueAt(value, now)

  if (state.kind === "future") {
    return `Nächster Kontaktschritt · automatisch am ${state.formattedDueAt}`
  }

  if (state.kind === "overdue") {
    return `Nächster Kontaktschritt · fällig seit ${state.formattedDueAt}`
  }

  return "Nächster Kontaktschritt · Fälligkeit nicht verfügbar"
}

const definition = (
  value: string,
  label: string,
  color: StatusColorName,
  icon: StatusDefinition["icon"],
  description: string,
): StatusDefinition<string> => ({ value, label, color, icon, description })

const richStatusTooltip = ({
  label,
  description,
}: {
  label: string
  description: string
  occurredAt?: string | null
  subjectLabel?: string | null
  recordedBy?: ContactStepOutcomeReadModel["recorded_by"]
}) => buildRichTooltip([description !== label ? tooltipText(description) : null])

const outcomeDescription: Record<string, string> = {
  COMPLETED: "Kontaktschritt abgeschlossen",
  APPOINTMENT_CONFIRMED: "Termin bestätigt",
  APPOINTMENT_SCHEDULED: "Termin vereinbart",
  APPOINTMENT_DECLINED: "Termin abgelehnt",
  NO_RESPONSE: "Keine Antwort",
  NOT_REACHED: "Nicht erreicht",
  INVALID_CONTACT: "Kontakt ungültig",
  WRONG_RECIPIENT: "Falscher Empfänger",
  DELIVERY_FAILED: "Versand fehlgeschlagen",
  TECHNICAL_FAILURE: "Versand fehlgeschlagen",
  MANUAL_CANCEL: "Übersprungen",
  WAIT_SKIPPED: "Wartezeit beendet",
  AUTOMATIC_SKIP: "Übersprungen",
  APPOINTMENT_CANCELED: "Nicht erforderlich",
  APPOINTMENT_COMPLETED: "Nicht erforderlich",
  APPOINTMENT_RESCHEDULED: "Nicht erforderlich",
  PLAN_CANCELED: "Plan abgebrochen",
  PRODUCT_RESOLVED: "Plan abgebrochen",
  UNKNOWN: "Abgebrochen",
}

const outcomeDetail: Record<string, string> = {
  APPOINTMENT_CONFIRMED: "Der Termin wurde bereits bestätigt. Dieser Kontaktversuch entfällt.",
  APPOINTMENT_CANCELED:
    "Der Termin wurde storniert; dieser Kontaktweg ist nicht mehr erforderlich.",
  APPOINTMENT_COMPLETED:
    "Der Termin wurde abgeschlossen; dieser Kontaktweg ist nicht mehr erforderlich.",
  APPOINTMENT_RESCHEDULED:
    "Der Termin wurde verschoben; dieser Kontaktweg ist nicht mehr erforderlich.",
  MANUAL_CANCEL: "Dieser einzelne Kontaktweg wurde manuell übersprungen.",
  WAIT_SKIPPED: "Die Wartezeit wurde beendet und der nächste Kontaktschritt freigegeben.",
  AUTOMATIC_SKIP: "Dieser Kontaktweg wurde automatisch übersprungen.",
  PLAN_CANCELED: "Der gesamte Kontaktplan wurde manuell abgebrochen.",
  PRODUCT_RESOLVED: "Der gesamte Kontaktplan wurde beendet.",
  UNKNOWN: "Der historische Abschlussgrund ist nicht eindeutig dokumentiert.",
}

const outcomeSubLabel = (outcome: ContactStepOutcomeReadModel) => {
  const occurredAt = formatDateTime(outcome.occurred_at)
  const actor = displayOutcomeActor(outcome.recorded_by)

  if (outcome.reason === "WAIT_SKIPPED") {
    return ["Nächster Kontaktschritt freigegeben", occurredAt].filter(Boolean).join(" · ")
  }

  if (outcome.reason === "APPOINTMENT_CONFIRMED") {
    return [
      `Termin bestätigt${outcome.subject_label ? ` durch ${outcome.subject_label}` : ""}`,
      occurredAt,
    ]
      .filter(Boolean)
      .join(" · ")
  }

  if (outcome.reason === "APPOINTMENT_CANCELED") {
    return ["Termin storniert", actor ? `durch ${actor}` : null, occurredAt]
      .filter(Boolean)
      .join(" · ")
  }

  if (outcome.reason === "APPOINTMENT_COMPLETED") {
    return ["Termin abgeschlossen", actor ? `durch ${actor}` : null, occurredAt]
      .filter(Boolean)
      .join(" · ")
  }

  if (outcome.reason === "APPOINTMENT_RESCHEDULED") {
    return ["Termin verschoben", actor ? `durch ${actor}` : null, occurredAt]
      .filter(Boolean)
      .join(" · ")
  }

  if (outcome.reason === "MANUAL_CANCEL") {
    return [actor ? `Durch ${actor}` : "Manuell", occurredAt].filter(Boolean).join(" · ")
  }

  if (outcome.reason === "AUTOMATIC_SKIP") {
    return ["Automatisch übersprungen", occurredAt].filter(Boolean).join(" · ")
  }

  return occurredAt
}

const presentationFromOutcome = (
  outcome: ContactStepOutcomeReadModel,
): CommunicationPlanStatusPresentation => {
  const label =
    outcome.kind === "NOT_REQUIRED"
      ? "Nicht erforderlich"
      : (outcomeDescription[outcome.reason] ?? "Kontaktschritt abgeschlossen")
  const description =
    outcome.reason === "APPOINTMENT_CONFIRMED" && outcome.kind !== "NOT_REQUIRED"
      ? "Dieser Kontaktweg hat den Termin bestätigt."
      : (outcomeDetail[outcome.reason] ?? label)
  const failure = ["DELIVERY_FAILED", "TECHNICAL_FAILURE"].includes(outcome.reason)
  const warning = ["NO_RESPONSE", "NOT_REACHED", "INVALID_CONTACT", "WRONG_RECIPIENT"].includes(
    outcome.reason,
  )
  const neutral =
    outcome.kind === "NOT_REQUIRED" ||
    [
      "MANUAL_CANCEL",
      "WAIT_SKIPPED",
      "AUTOMATIC_SKIP",
      "APPOINTMENT_CANCELED",
      "APPOINTMENT_COMPLETED",
      "APPOINTMENT_RESCHEDULED",
      "PLAN_CANCELED",
      "PRODUCT_RESOLVED",
      "UNKNOWN",
    ].includes(outcome.reason)

  return {
    definition: definition(
      outcome.reason,
      label,
      failure ? "rose" : warning ? "amber" : neutral ? "neutral" : "emerald",
      failure
        ? mdiAlertCircleOutline
        : outcome.reason === "WAIT_SKIPPED"
          ? mdiTimerOffOutline
          : neutral
            ? mdiCancel
            : mdiCheckBold,
      description,
    ),
    subLabel: outcomeSubLabel(outcome),
    tooltipHtml: richStatusTooltip({
      label,
      description,
      occurredAt: outcome.occurred_at,
      subjectLabel: outcome.subject_label,
      recordedBy: outcome.recorded_by,
    }),
    hint: null,
  }
}

const legacyOutcome = (step: ContactOrderStep): ContactStepOutcomeReadModel | null => {
  const status = normalizedCode(step.status)
  const result = normalizedCode(step.result_status)
  const meta = (step.result_meta ?? {}) as Record<string, unknown>
  const rawReason = normalizedCode(meta.reason)
  const reasonMap: Record<string, ContactStepOutcomeReadModel["reason"]> = {
    MANUAL_CANCEL: "MANUAL_CANCEL",
    WAIT_SKIPPED: "WAIT_SKIPPED",
    WRONG_RECIPIENT: "WRONG_RECIPIENT",
    DELIVERY_FAILED: "DELIVERY_FAILED",
    NO_RESPONSE: "NO_RESPONSE",
    INVALID_CONTACT: "INVALID_CONTACT",
    APPOINTMENT_CONFIRMED: "APPOINTMENT_CONFIRMED",
    APPOINTMENT_CANCELED: "APPOINTMENT_CANCELED",
    APPOINTMENT_COMPLETED: "APPOINTMENT_COMPLETED",
    APPOINTMENT_RESCHEDULED: "APPOINTMENT_RESCHEDULED",
    PLAN_CANCELED: "PLAN_CANCELED",
  }
  const resultMap: Record<string, ContactStepOutcomeReadModel["reason"]> = {
    COMPLETED: "COMPLETED",
    CONFIRMED: "APPOINTMENT_CONFIRMED",
    APPOINTMENT_CONFIRMED: "APPOINTMENT_CONFIRMED",
    APPOINTMENT_APPROVED: "APPOINTMENT_CONFIRMED",
    APPOINTMENT_SCHEDULED: "APPOINTMENT_SCHEDULED",
    DECLINED: "APPOINTMENT_DECLINED",
    APPOINTMENT_DECLINED: "APPOINTMENT_DECLINED",
    NO_RESPONSE: "NO_RESPONSE",
    NOT_REACHED: "NOT_REACHED",
    INVALID_CONTACT: "INVALID_CONTACT",
    APPOINTMENT_CANCELED: "APPOINTMENT_CANCELED",
    WRONG_RECIPIENT: "WRONG_RECIPIENT",
    DELIVERY_FAILED: "DELIVERY_FAILED",
    TECHNICAL_FAILURE: "TECHNICAL_FAILURE",
    SKIPPED: "AUTOMATIC_SKIP",
  }
  const explicitResultReason = resultMap[result]
  const reason =
    reasonMap[rawReason] ??
    explicitResultReason ??
    (status === "CANCELED" ? "UNKNOWN" : null) ??
    (status === "DONE" ? "COMPLETED" : null)

  if (!reason) return null

  return {
    kind: explicitResultReason
      ? "RESULT"
      : reason === "MANUAL_CANCEL" || reason === "AUTOMATIC_SKIP"
        ? "SKIPPED"
        : reason.startsWith("APPOINTMENT_") &&
            !["APPOINTMENT_SCHEDULED", "APPOINTMENT_DECLINED"].includes(reason)
          ? "NOT_REQUIRED"
          : reason === "PLAN_CANCELED" || reason === "PRODUCT_RESOLVED"
            ? "PLAN_CLOSED"
            : reason === "UNKNOWN"
              ? "UNKNOWN_CANCELED"
              : "RESULT",
    reason,
    occurred_at: step.result_recorded_at ?? null,
    subject_label: normalizedText(meta.subject_label),
    recorded_by: null,
  }
}

const isManualLetter = (entry: AdaptedCommunicationPlanFeedEntry) => {
  if (entry.kind !== "contact_step" || normalizedCode(entry.step.channel) !== "LETTER") return false

  return (
    normalizedCode(entry.step.execution_mode) === "MANUAL_LETTER_DELIVERY" ||
    normalizedCode(entry.extension?.letter_delivery?.delivery_mode) === "MANUAL"
  )
}

const providerLetterStatus = (
  entry: AdaptedCommunicationPlanFeedEntry,
): CommunicationPlanStatusPresentation | null => {
  const communication = entry.communication?.communication ?? null
  const channel = normalizedCode(
    entry.kind === "contact_step" ? entry.step.channel : communication?.channel,
  )

  if (channel !== "LETTER" || isManualLetter(entry)) return null

  const progress = entry.extension?.provider_progress
  const status = normalizedCode(
    progress?.status || progress?.provider_status || communication?.provider_status,
  )
  const delivery = normalizedCode(communication?.delivery_status)
  const lifecycle = normalizedCode(communication?.lifecycle_status)
  const detail = normalizedText(progress?.detail)
  let label = "Brief wird versendet"
  let subLabel = detail || "Briefauftrag wird verarbeitet"
  let color: StatusColorName = "blue"
  let icon = mdiSendClockOutline

  if (
    ["FAILED", "ERROR", "REJECTED"].includes(status) ||
    delivery === "FAILED" ||
    lifecycle === "FAILED"
  ) {
    label = "Fehler beim Briefversand"
    subLabel = detail || "Manuelle Prüfung erforderlich"
    color = "rose"
    icon = mdiAlertCircleOutline
  } else if (
    ["SENT", "DELIVERED", "READ", "COMPLETED"].includes(status) ||
    ["SENT", "DELIVERED", "READ"].includes(delivery)
  ) {
    label = "Brief versendet"
    subLabel = detail || (delivery === "DELIVERED" ? "Zugestellt" : "An Briefdienst übergeben")
    color = "emerald"
    icon = mdiCheckBold
  } else if (status === "HELD") {
    label = "Briefversand angehalten"
    subLabel = detail || "Versand pausiert"
    color = "amber"
    icon = mdiProgressClock
  }

  const tooltipHtml = buildRichTooltip([
    tooltipTitle(label),
    tooltipText(subLabel),
    progress?.dispatch_date
      ? tooltipField("Versanddatum", formatDate(progress.dispatch_date))
      : null,
    progress?.tracking_code ? tooltipField("Referenz", progress.tracking_code) : null,
  ])

  return {
    definition: definition(status || delivery || "LETTER_PROCESSING", label, color, icon, subLabel),
    subLabel,
    tooltipHtml,
    hint: null,
  }
}

const manualLetterStatus = (
  step: ContactOrderStep,
  now: number,
  existingHint: ContactOrderPlanHint | null,
): CommunicationPlanStatusPresentation => {
  const due = step.due_at ? new Date(step.due_at) : null
  const validDue = due && !Number.isNaN(due.getTime()) ? due : null
  const subLabel =
    validDue && validDue.getTime() > now
      ? `Zustellung ab ${formatDate(step.due_at)} möglich`
      : "Jetzt zustellbar"
  const description =
    "E-Mail, Nachricht und Anruf laufen unabhängig von der Briefzustellung weiter."

  return {
    definition: definition(
      "MANUAL_LETTER",
      "Brief separat",
      validDue && validDue.getTime() > now ? "neutral" : "orange",
      mdiEmailOutline,
      description,
    ),
    subLabel,
    tooltipHtml: buildRichTooltip([tooltipText(description)]),
    hint: existingHint
      ? { ...existingHint, color: validDue && validDue.getTime() > now ? "neutral" : "orange" }
      : null,
  }
}

const phaseStatus = (step: ContactOrderStep, now: number): CommunicationPlanStatusPresentation => {
  const status = normalizedCode(step.status) || "UNKNOWN"
  const waitUntil = formatDateTime(step.response_deadline_at)
  const nextAttempt = formatDateTime(step.next_action_at || step.due_at)
  const values: Record<
    string,
    { label: string; subLabel: string; color: StatusColorName; icon: StatusDefinition["icon"] }
  > = {
    READY: {
      label: "Anstehend",
      subLabel: readyDueSubLabel(step.due_at, now),
      color: "blue",
      icon: mdiClockOutline,
    },
    BLOCKED: {
      label: "Wartet",
      subLabel: "Vorheriger Kontaktschritt noch offen",
      color: "blue",
      icon: mdiLockOutline,
    },
    EXECUTING: {
      label: "Wird ausgeführt",
      subLabel: "Kontaktschritt wird gerade gestartet",
      color: "blue",
      icon: mdiProgressClock,
    },
    DISPATCHED: {
      label: "Kontakt gestartet",
      subLabel: "Rückmeldung steht aus",
      color: "blue",
      icon: mdiSendClockOutline,
    },
    WAITING: {
      label: "Wartet auf Antwort",
      subLabel: waitUntil ? `Antwort bis ${waitUntil}` : "Rückmeldung steht aus",
      color: "blue",
      icon: mdiProgressClock,
    },
    RETRY_SCHEDULED: {
      label: "Neuer Versuch",
      subLabel:
        step.next_action_at && new Date(step.next_action_at).getTime() > now
          ? `Automatisch am ${nextAttempt}`
          : "Erneuter Versuch ist fällig",
      color: "amber",
      icon: mdiClockOutline,
    },
    ERROR: {
      label: "Fehler",
      subLabel: "Manuelle Prüfung erforderlich",
      color: "rose",
      icon: mdiAlertCircleOutline,
    },
  }
  const current = values[status] ?? {
    label: "Status unbekannt",
    subLabel: "Manuelle Prüfung erforderlich",
    color: "blue" as StatusColorName,
    icon: mdiProgressClock,
  }
  const description = current.subLabel

  return {
    definition: definition(status, current.label, current.color, current.icon, description),
    subLabel: current.subLabel,
    tooltipHtml: buildRichTooltip([tooltipTitle(current.label), tooltipText(description)]),
    hint: null,
  }
}

const communicationLifecycleHint = (
  item: CaseCommunicationTimelineItem | null,
  statusLabel: string,
): ContactOrderPlanHint | null => {
  if (!item) return null

  const communication = item.communication
  const delivery = normalizedCode(communication.delivery_status)
  const lifecycle = normalizedCode(communication.lifecycle_status)
  const channel = normalizedCode(communication.channel)
  let label: string | null = null

  if (delivery === "READ") label = channel === "WHATSAPP" ? "WhatsApp gelesen" : "Nachricht gelesen"
  else if (delivery === "DELIVERED") label = "Zugestellt"
  else if (["SENT", "ACCEPTED"].includes(delivery)) {
    label =
      channel === "EMAIL"
        ? "E-Mail versendet"
        : channel === "LETTER"
          ? "Brief versendet"
          : "Versendet"
  } else if (["QUEUED", "IN_PROGRESS"].includes(lifecycle) || delivery === "QUEUED")
    label = "Versand vorbereitet"
  else if (lifecycle === "FAILED" || delivery === "FAILED") label = "Versand fehlgeschlagen"

  if (!label || label === statusLabel) return null

  const occurredAt =
    communication.read_at ||
    communication.delivered_at ||
    communication.sent_at ||
    communication.failed_at ||
    communication.updated_at

  return {
    label,
    subLabel: formatDateTime(occurredAt),
    color: lifecycle === "FAILED" || delivery === "FAILED" ? "rose" : "blue",
    icon:
      lifecycle === "FAILED" || delivery === "FAILED"
        ? mdiAlertCircleOutline
        : mdiMessageTextOutline,
    tooltipHtml: buildRichTooltip([
      tooltipTitle(label),
      tooltipField("Zeitpunkt", formatDateTime(occurredAt)),
    ]),
  }
}

const standaloneCommunicationStatus = (
  item: CaseCommunicationTimelineItem,
): CommunicationPlanStatusPresentation => {
  const communication = item.communication
  const delivery = normalizedCode(communication.delivery_status)
  const lifecycle = normalizedCode(communication.lifecycle_status)

  if (lifecycle === "SUPPRESSED") {
    const subLabel = formatDateTime(communication.suppressed_at || communication.created_at)

    return {
      definition: definition(
        "SUPPRESSED",
        "Ohne Nachricht",
        "neutral",
        mdiCloseCircleOutline,
        "Nicht versendet",
      ),
      subLabel,
      tooltipHtml: buildRichTooltip([
        tooltipTitle("Ohne Nachricht"),
        tooltipText("Nicht versendet"),
        tooltipField("Erfasst am", subLabel),
      ]),
      hint: null,
    }
  }

  if (delivery === "READ") {
    const label =
      normalizedCode(communication.channel) === "WHATSAPP"
        ? "WhatsApp gelesen"
        : "Nachricht gelesen"
    const subLabel = formatDateTime(communication.read_at)

    return {
      definition: definition(
        "READ",
        label,
        "forest",
        mdiCheckBold,
        "Lesebestätigung des Anbieters erhalten.",
      ),
      subLabel,
      tooltipHtml: buildRichTooltip([
        tooltipTitle(label),
        tooltipText(
          "Lesebestätigung des Anbieters erhalten. Keine ausdrückliche Terminbestätigung.",
        ),
        tooltipField("Lesebestätigung erhalten am", subLabel),
      ]),
      hint: null,
    }
  }

  const failed = lifecycle === "FAILED" || delivery === "FAILED"
  const completed =
    ["READ", "DELIVERED", "SENT", "MANUAL_DELIVERED"].includes(delivery) ||
    lifecycle === "COMPLETED"
  const label = failed
    ? "Versand fehlgeschlagen"
    : completed
      ? "Versendet"
      : lifecycle === "IN_PROGRESS"
        ? "Wird ausgeführt"
        : "Geplant"
  const occurredAt =
    communication.read_at ||
    communication.delivered_at ||
    communication.sent_at ||
    communication.failed_at ||
    communication.created_at
  const subLabel = formatDateTime(occurredAt)
  const description = failed
    ? "Die historische Kommunikation ist fehlgeschlagen."
    : completed
      ? "Die historische Kommunikation wurde versendet."
      : "Historischer Kommunikationsstatus."

  return {
    definition: definition(
      delivery || lifecycle,
      label,
      failed ? "rose" : completed ? "emerald" : "blue",
      failed ? mdiAlertCircleOutline : completed ? mdiCheckBold : mdiProgressClock,
      description,
    ),
    subLabel,
    tooltipHtml: buildRichTooltip([
      tooltipTitle(label),
      tooltipText(description),
      tooltipField("Zeitpunkt", subLabel),
    ]),
    hint: null,
  }
}

export const buildCommunicationPlanStatusPresentation = ({
  entry,
  existingHint = null,
  now = Date.now(),
}: {
  entry: AdaptedCommunicationPlanFeedEntry
  existingHint?: ContactOrderPlanHint | null
  now?: number
}): CommunicationPlanStatusPresentation => {
  if (entry.kind === "contact_step" && !entry.step.automatic_delivery_committed) {
    if (
      entry.step.result_status === "SKIPPED" &&
      entry.step.result_meta?.reason_code === "whatsapp_read"
    ) {
      const label = "Nicht erforderlich · WhatsApp gelesen"
      const description =
        "Der Schritt entfällt, weil die zugehörige Terminankündigung per WhatsApp als gelesen gemeldet wurde."

      return {
        definition: definition("SKIPPED", label, "neutral", mdiCancel, description),
        subLabel: formatDateTime(entry.step.result_recorded_at),
        tooltipHtml: buildRichTooltip([tooltipTitle(label), tooltipText(description)]),
        hint: null,
      }
    }
  }

  if (
    entry.kind === "contact_step" &&
    normalizedCode(entry.step.channel) === "LETTER" &&
    entry.extension?.letter_dispatch?.needs_review &&
    !["CANCELED", "CANCELLED", "SKIPPED", "DONE", "COMPLETED"].includes(
      normalizedCode(entry.step.status),
    )
  ) {
    const description = "Die Briefzustellung benötigt eine manuelle Prüfung in der Inbox."

    return {
      definition: definition(
        "LETTER_REVIEW",
        "Briefversand prüfen",
        "orange",
        mdiAlertCircleOutline,
        description,
      ),
      subLabel: "Manuelle Prüfung erforderlich",
      tooltipHtml: buildRichTooltip([tooltipText(description)]),
      hint: null,
    }
  }

  const providerStatus = providerLetterStatus(entry)

  if (providerStatus) return providerStatus

  if (entry.kind === "contact_step") {
    const outcome = entry.extension?.step_outcome ?? legacyOutcome(entry.step)

    if (outcome) {
      const presentation = presentationFromOutcome(outcome)

      return {
        ...presentation,
        hint:
          communicationLifecycleHint(entry.communication, presentation.definition.label) ??
          existingHint,
      }
    }

    if (isManualLetter(entry)) {
      const dispatch = entry.extension?.letter_dispatch

      if (dispatch?.planned_mode === "AUTOMATIC" && !dispatch.manual_action_required) {
        const due = entry.step.due_at ? new Date(entry.step.due_at) : null
        const description =
          "Der Brief wird bei Fälligkeit automatisch beauftragt, sofern er noch erforderlich ist. Noch kein Versandauftrag."

        return {
          definition: definition(
            "AUTOMATIC_LETTER_PLANNED",
            "Automatischer Briefversand",
            "blue",
            mdiSendClockOutline,
            description,
          ),
          subLabel:
            due && due.getTime() > now
              ? `Vorgesehen ab ${formatDate(entry.step.due_at)}`
              : "Automatische Beauftragung ausstehend",
          tooltipHtml: buildRichTooltip([tooltipText(description)]),
          hint: null,
        }
      }

      return manualLetterStatus(entry.step, now, existingHint)
    }

    const phase = phaseStatus(entry.step, now)

    return {
      ...phase,
      hint: communicationLifecycleHint(entry.communication, phase.definition.label) ?? existingHint,
    }
  }

  return standaloneCommunicationStatus(entry.communication)
}
